<div class="w-full max-w-full relative">
    <!-- Header -->
    <div class="mb-4 relative z-20">
        <div
            class="bg-white dark:bg-surface-1 border border-[#EAEAEA] dark:border-line rounded-xl p-4 px-6 shadow-sm">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 class="text-lg font-bold text-[#141437] dark:text-white">
                        Release
                    </h1>
                    <p class="text-sm text-slate-500 dark:text-slate-400">
                        Last Release: {{ latestReleaseVersion() }}
                    </p>
                </div>
                <div class="flex items-center gap-3">
                    <app-button variant="outline" (buttonClick)="exportToExcel()" [iconName]="'download'"
                        className="flex items-center justify-center gap-2 border-[#AD46FF] text-[#AD46FF] hover:bg-[#AD46FF]/10 transition-all px-4 py-2 h-[41px]">
                        <span class="font-medium whitespace-nowrap">Export</span>
                    </app-button>
                    <app-button variant="default" (buttonClick)="openAddUserPanel()" [iconName]="'circlePlus'"
                        className="flex items-center justify-center gap-2 shadow-[0px_6px_12px_-6px_#AD46FF] hover:shadow-[0px_4px_8px_-4px_#AD46FF] transition-all bg-[#AD46FF] hover:bg-[#AD46FF] border-none text-white">
                        <span class="font-medium whitespace-nowrap">Add New Release</span>
                    </app-button>
                </div>
            </div>
        </div>
    </div>

    <!-- Filters -->
    <div class="mb-6 relative z-30 mx-auto">
        <div class="card-bg rounded-2xl p-6">
            <div class="flex flex-col lg:flex-row gap-4">
                <!-- Search -->
                <div class="flex-1 relative z-40">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Search
                    </label>
                    <div class="relative">
                        <div
                            class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400/80">
                            <app-icon name="search" [size]="17"></app-icon>
                        </div>
                        <input type="text" placeholder="Search"
                            class="w-full h-[48px] pl-11 pr-4 bg-white dark:bg-surface-1 border border-[#EAEAEA] dark:border-line rounded-xl text-sm focus:outline-none focus:border-purple-500 transition-all dark:text-white placeholder:text-[#BFBFC7] dark:placeholder:text-ink-subtle"
                            [ngModel]="searchTerm()" (input)="onSearch(searchInput.value)"
                            (keyup.enter)="applyFilters()" #searchInput>
                    </div>
                </div>

                <!-- Product Filter -->
                <div class="flex-1 relative z-30">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Product
                    </label>
                    <app-dropdown [options]="productOptions()" [selectedValue]="selectedProduct()"
                        selectionMode="single" placeholder="Filter by Product" [fullWidth]="true" size="lg"
                        variant="themeBase" [placeholderColor]="'text-[#BFBFC7] dark:text-ink-subtle'"
                        (selectionChange)="onProductFilter($event)" class="w-full relative z-50" />
                </div>

                <!-- Platform -->
                <div class="flex-1 relative z-20">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Release Platform
                    </label>
                    <app-dropdown [options]="platformOptions" [selectedValue]="selectedPlatform()"
                        selectionMode="single" placeholder="Filter by Platform" [fullWidth]="true" size="lg"
                        variant="themeBase" [placeholderColor]="'text-[#BFBFC7] dark:text-ink-subtle'"
                        (selectionChange)="onPlatformFilter($event)" class="w-full relative z-50" />
                </div>

                <!-- Available To -->
                <div class="flex-1 relative z-10">
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Available to
                    </label>
                    <app-dropdown [options]="availableToOptions" [selectedValue]="selectedAvailableTo()"
                        selectionMode="single" placeholder="Available to" [fullWidth]="true" size="lg"
                        variant="themeBase" [placeholderColor]="'text-[#BFBFC7] dark:text-ink-subtle'"
                        (selectionChange)="onAvailableToFilter($event)" class="w-full relative z-50" />
                </div>

                <div class="flex items-end z-10 gap-3">
                    <app-button variant="primary" (buttonClick)="applyFilters()"
                        className="h-[48px] px-6 text-white bg-[#0F172A] hover:bg-[#1E293B] transition-all duration-200 rounded-xl border-none shadow-none">
                        <span class="font-medium whitespace-nowrap">Apply Filters</span>
                    </app-button>

                    <app-button
                        [disabled]="!selectedPlatform() && !searchTerm() && !selectedAvailableTo() && !selectedProduct()"
                        variant="secondary" (buttonClick)="clearFilters()"
                        className="h-[48px] px-6 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 rounded-xl transition-all duration-200 font-medium">
                        Clear Filters
                    </app-button>
                </div>
            </div>
        </div>
    </div>

    <!-- Table -->
    @if (isLoading()) {
    <div
        class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg border border-white/20 dark:border-slate-700/50 rounded-2xl p-12 shadow-lg relative z-10">
        <div class="flex items-center justify-center">
            <div class="flex items-center gap-3">
                <div class="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span class="text-slate-600 dark:text-slate-400 font-medium">Loading release data...</span>
            </div>
        </div>
    </div>
    } @else if (processedReleaseData.length > 0) {
    <div class="card-bg rounded-2xl overflow-hidden relative z-10">
        <div
            class="px-6 py-4 bg-gradient-to-r from-slate-50/50 to-blue-50/50 dark:from-slate-700/50 dark:to-slate-600/50 border-b border-slate-200/50 dark:border-slate-600/50">
            <h2 class="text-lg font-medium text-slate-800 dark:text-slate-200">
                Release List
            </h2>
            <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {{ totalItems() }} total records found
            </p>
        </div>

        <div class="p-6">
            <app-paginated-table (actionSelected)="selectedRowItem($event)" [tableData]="processedReleaseData"
                [headers]="tableHeaders" [isCheckboxShow]="false" [totalItems]="totalItems()" [pageSize]="pageSize()"
                [currentPage]="page()" (pageChange)="onPageChange($event)" [serverSide]="true"
                [tableActionOptions]="{showToggleButton: true, toggleOptions:tableActionOptions}" />
        </div>
    </div>
    } @else {
    <div
        class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-lg border border-white/20 dark:border-slate-700/50 rounded-2xl p-12 shadow-lg text-center relative z-10">
        <div class="max-w-md mx-auto">
            <div
                class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
                <app-icon name="inbox" [size]="32" className="text-blue-600 dark:text-blue-400"></app-icon>
            </div>
            <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                No Releases Found
            </h3>
            <p class="text-slate-600 dark:text-slate-400 mb-6">
                Start by adding your first platform release documentation.
            </p>
        </div>
    </div>
    }

    <!-- Slide Panel: Add/Edit Release -->
    @if (panelOpen()) {
    <app-slide-panel [open]="panelOpen()" [title]="isEditing() ? 'Edit Release' : 'Add New Release'"
        (closed)="closePanel()" class="flex-1 text-[#141437] dark:text-white"
        panelClass="min-w-[600px] lg:min-w-[700px] max-w-[800px] h-full flex flex-col overflow-hidden card-bg border-l border-[#EAEAEA] dark:border-line">
        <form [formGroup]="form" (ngSubmit)="submitForm()" class="flex flex-col flex-1 overflow-hidden h-full">
            <section class="flex flex-col gap-6 flex-1 overflow-y-auto p-8">
                <!-- Edit Mode Header -->
                @if (isEditing()) {
                <div class="mb-4">
                    <h2 class="text-xl font-bold text-[#141437] dark:text-white">Version: {{
                        form.get('versionName')?.value }}</h2>
                    <div class="flex gap-6 mt-1 text-sm text-slate-500 dark:text-slate-400">
                        <span>Release Platform: <span class="font-medium text-[#141437] dark:text-white">{{
                                getPlatformLabel($safeNavigationMigration(form.get('platforms')?.value)) }}</span></span>
                        <span>Release Type: <span class="font-medium text-[#141437] dark:text-white">{{
                                getTypeLabel($safeNavigationMigration(form.get('type')?.value)) }}</span></span>
                    </div>
                </div>
                }

                @if (!isEditing()) {
                <!-- Platform Selection -->
                <div class="space-y-3">
                    <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Select Release
                        Platform<span class="text-red-500 dark:text-red-400 ml-0.5">*</span></label>
                    <div class="flex flex-wrap gap-3">
                        @for (opt of platformOptions; track opt.value) {
                        <button type="button" (click)="form.get('platforms')?.setValue(opt.value)"
                            [class]="'flex items-center gap-2 px-4 py-2 rounded-full border transition-all ' + 
                                (form.get('platforms')?.value === opt.value ? 
                                'bg-[#AD46FF]/10 border-[#AD46FF] text-[#AD46FF] shadow-sm' : 
                                'bg-white dark:bg-surface-1 border-[#EAEAEA] dark:border-line text-slate-500 dark:text-slate-400')">
                            <app-icon [name]="opt.iconName!" [size]="16"></app-icon>
                            <span class="text-sm font-medium">{{ opt.label }}</span>
                        </button>
                        }
                    </div>
                </div>

                <!-- Release Type -->
                <div class="space-y-3">
                    <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Select Release
                        Type<span class="text-red-500 dark:text-red-400 ml-0.5">*</span></label>
                    <div class="flex flex-wrap gap-4">
                        @for (opt of typeOptions; track opt.value) {
                        <label class="flex items-center gap-2 cursor-pointer group">
                            <div class="relative w-5 h-5">
                                <input type="radio" [value]="opt.value" formControlName="type" class="sr-only">
                                <div
                                    [class]="'w-5 h-5 rounded-full border-2 transition-all ' + 
                                        (form.get('type')?.value === opt.value ? 'border-[#AD46FF]' : 'border-slate-300 dark:border-slate-600')">
                                </div>
                                @if (form.get('type')?.value === opt.value) {
                                <div class="absolute inset-0 m-auto w-2.5 h-2.5 rounded-full bg-[#AD46FF]"></div>
                                }
                            </div>
                            <span
                                [class]="'text-sm transition-all ' + (form.get('type')?.value === opt.value ? 'text-[#AD46FF] font-medium' : 'text-slate-600 dark:text-slate-400')">
                                {{ opt.label }}
                            </span>
                        </label>
                        }
                    </div>
                </div>

                <!-- Product & Version -->
                <div class="grid grid-cols-2 gap-6">
                    <div class="flex flex-col">
                        <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Product<span
                                class="text-red-500 dark:text-red-400 ml-0.5">*</span></label>
                        <app-dropdown [options]="productOptions()" formControlName="productId"
                            placeholder="Select Product" [fullWidth]="true" size="lg" variant="themeBase"
                            [placeholderColor]="'text-[#BFBFC7] dark:text-ink-subtle'"
                            [error]="getErrorMessage('productId')" />
                    </div>
                    <app-input variant="themeBase" placeholder="40.2.0" label="Version"
                        formControlName="versionName" [error]="getErrorMessage('versionName')"></app-input>
                </div>
                }

                <!-- Available To -->
                <div class="space-y-3">
                    <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Available to<span
                            class="text-red-500 dark:text-red-400 ml-0.5">*</span></label>
                    <div class="flex flex-wrap gap-2">
                        @for (opt of availableToOptions; track opt.value) {
                        <button type="button" (click)="toggleAvailableTo(opt.value)"
                            [class]="'px-4 py-1.5 rounded-full border text-xs font-medium transition-all ' + 
                                (form.get('availableTo')?.value?.includes(opt.value) ? 
                                'bg-purple-100 border-[#AD46FF] text-[#AD46FF]' : 
                                'bg-white dark:bg-surface-1 border-[#EAEAEA] dark:border-line text-slate-500 dark:text-slate-400')">
                            {{ opt.label }}
                        </button>
                        }
                    </div>
                </div>

                <!-- Date & Title -->
                <div class="grid grid-cols-2 gap-6">
                    <app-input type="text" mask="date" variant="themeBase" label="Release Date" [required]="true"
                        formControlName="releaseDate" [error]="getErrorMessage('releaseDate')"></app-input>
                    <app-input variant="themeBase" placeholder="Enter release title" label="Title" [required]="true"
                        formControlName="releaseTitle" [error]="getErrorMessage('releaseTitle')"></app-input>
                </div>

                <!-- Description -->
                <div class="space-y-2">
                    <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Description<span
                            class="text-red-500 dark:text-red-400 ml-0.5">*</span></label>
                    <textarea formControlName="description" rows="4"
                        placeholder="Added full receivable tracking including invoice, due date & status"
                        class="w-full p-4 bg-white dark:bg-surface-1 border border-[#EAEAEA] dark:border-line rounded-xl text-sm focus:outline-none focus:border-[#AD46FF] transition-all dark:text-white resize-none"
                        [class.border-red-500]="formSubmitted() && form.get('description')?.invalid"></textarea>
                </div>

                <!-- Notes -->
                <app-input variant="themeBase" placeholder="Enter Release Notes" label="Notes (Optional)"
                    formControlName="notes"></app-input>

                <!-- Links -->
                <div class="space-y-3">
                    <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Links
                        (Optional)</label>
                    <div class="flex gap-2">
                        <input type="text" [ngModel]="newLink()" (ngModelChange)="newLink.set($event)"
                            [ngModelOptions]="{standalone: true}" placeholder="Enter Links"
                            class="flex-1 h-[45px] px-4 bg-white dark:bg-surface-1 border border-[#EAEAEA] dark:border-line rounded-xl text-sm focus:outline-none focus:border-[#AD46FF] transition-all dark:text-white">
                        <app-button variant="primary" (buttonClick)="addLink()"
                            className="h-[45px] px-6 bg-[#1E6AFD] hover:bg-[#1E6AFD]/90 text-white rounded-xl">
                            + Add
                        </app-button>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-2">
                        @for (link of links(); track link) {
                        <div
                            class="flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg group">
                            <span class="text-xs text-[#1E6AFD]">{{ link }}</span>
                            <button type="button" (click)="removeLink(link)" class="text-blue-400 hover:text-blue-600">
                                <app-icon name="x" [size]="12"></app-icon>
                            </button>
                        </div>
                        }
                    </div>
                </div>

                <!-- Tags -->
                <div class="space-y-3">
                    <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Tags
                        (Optional)</label>
                    <div class="flex gap-2">
                        <input type="text" [ngModel]="newTag()" (ngModelChange)="newTag.set($event)"
                            [ngModelOptions]="{standalone: true}" placeholder="Enter Tags"
                            class="flex-1 h-[45px] px-4 bg-white dark:bg-surface-1 border border-[#EAEAEA] dark:border-line rounded-xl text-sm focus:outline-none focus:border-[#AD46FF] transition-all dark:text-white">
                        <app-button variant="primary" (buttonClick)="addTag()"
                            className="h-[45px] px-6 bg-[#1E6AFD] hover:bg-[#1E6AFD]/90 text-white rounded-xl">
                            + Add
                        </app-button>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-2">
                        @for (tag of tags(); track tag) {
                        <span
                            class="flex items-center gap-2 px-3 py-1 bg-[#F9F5FF] dark:bg-purple-900/20 border border-[#E9D7FE] dark:border-purple-800 rounded-lg group">
                            <span class="text-xs text-[#6941C6]">{{ tag }}</span>
                            <button type="button" (click)="removeTag(tag)"
                                class="text-purple-400 hover:text-purple-600">
                                <app-icon name="x" [size]="12"></app-icon>
                            </button>
                        </span>
                        }
                    </div>
                </div>

            </section>

            <!-- Footer -->
            <div
                class="p-8 sticky bottom-0 bg-white dark:bg-surface-1 border-t border-[#EAEAEA] dark:border-line mt-auto">
                <app-button type="submit" variant="default" className="h-[44px] px-10 shadow-lg shadow-[#AD46FF]/30"
                    [disabled]="isLoading()">
                    <span class="text-sm font-medium">{{ isLoading() ? 'Updating...' : (isEditing() ? 'Update this
                        Release' : 'Add New Release') }}</span>
                </app-button>
            </div>
        </form>
    </app-slide-panel>
    }

    <!-- Release Detail Dialog Template -->
    <ng-template #releaseDetailTemplate>
        <div class="relative w-full max-w-2xl bg-white dark:bg-surface-1 rounded-3xl shadow-2xl overflow-hidden">
            <!-- Header -->
            <div class="p-8 pb-4">
                <div class="flex items-center justify-between mb-2">
                    <h2 class="text-3xl font-bold text-slate-800 dark:text-white">Release Details</h2>
                    <button (click)="closeDialog()"
                        class="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <app-icon name="x" [size]="24" class="text-slate-400"></app-icon>
                    </button>
                </div>
                <div
                    class="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-4">
                    <span>Version: {{ selectedRelease()?.versionName }}</span>
                    <span>Release Date: {{ $safeNavigationMigration(selectedRelease()?.releaseDate) | date:'MM/dd/yyyy' }}</span>
                </div>
            </div>

            <!-- Content -->
            <div class="px-8 pb-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <!-- Title & Description -->
                <div class="space-y-4">
                    <h3 class="text-xl font-bold text-slate-800 dark:text-white">{{ selectedRelease()?.releaseTitle }}
                    </h3>
                    <p class="text-slate-600 dark:text-slate-400 leading-relaxed">
                        {{ selectedRelease()?.description }}
                    </p>

                    <!-- Type & Platform Pills -->
                    <div class="flex flex-wrap gap-2">
                        <span
                            class="px-4 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                            {{ $safeNavigationMigration(selectedRelease()?.type) | titlecase }}
                        </span>
                        @for (platform of (selectedRelease()?.platforms || []); track platform) {
                        <span
                            class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                            <app-icon [name]="platform === 'WEB' ? 'globe' : 'smartphone'" [size]="16"></app-icon>
                            {{ platform | titlecase }}
                        </span>
                        }
                    </div>
                </div>

                <!-- Tags & Links -->
                <div class="pt-8 border-t border-slate-100 dark:border-slate-800">
                    <h4 class="text-lg font-bold text-slate-800 dark:text-white mb-4">Tags & Links</h4>
                    <div class="space-y-3">
                        @for (link of (selectedRelease()?.linkTags || []); track $index) {
                        <div class="flex items-center gap-4">
                            <span
                                class="px-3 py-1 rounded border border-purple-200 text-purple-600 text-xs font-medium bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800">
                                {{ link.label }}
                            </span>
                            <a [href]="link.url" target="_blank"
                                class="text-blue-500 hover:text-blue-600 text-sm truncate underline decoration-blue-200 underline-offset-4">
                                {{ link.url }}
                            </a>
                        </div>
                        }
                        @if (!(selectedRelease()?.linkTags?.length)) {
                        <div class="text-sm text-slate-400 italic">No tags or links provided.</div>
                        }
                    </div>
                </div>

                <!-- Notes -->
                <div class="pt-8 border-t border-slate-100 dark:border-slate-800">
                    <h4 class="text-lg font-bold text-slate-800 dark:text-white mb-4">Notes</h4>
                    <ul class="space-y-2 list-disc list-inside text-sm text-slate-600 dark:text-slate-400">
                        @for (note of (selectedRelease()?.notes || '').split('\n'); track $index) {
                        @if (note.trim()) {
                        <li>{{ note }}</li>
                        }
                        }
                        @if (!selectedRelease()?.notes) {
                        <li class="list-none text-slate-400 italic">No additional notes.</li>
                        }
                    </ul>
                </div>

                <!-- Other Information -->
                <div class="pt-8 border-t border-slate-100 dark:border-slate-800">
                    <h4 class="text-lg font-bold text-slate-800 dark:text-white mb-4">Other Information</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <span class="font-semibold text-slate-700 dark:text-slate-300">Available to:</span>
                            <p class="text-slate-600 dark:text-slate-400">{{ selectedRelease()?.availableTo?.join(', ')
                                || 'N/A' }}</p>
                        </div>
                        <div>
                            <span class="font-semibold text-slate-700 dark:text-slate-300">Created by:</span>
                            <p class="text-slate-600 dark:text-slate-400">Admin (Jan 19, 2025 • 3:45 PM)</p>
                        </div>
                        <div>
                            <span class="font-semibold text-slate-700 dark:text-slate-300">Last edited by:</span>
                            <p class="text-slate-600 dark:text-slate-400">Admin (Jan 20, 2025 • 9:15 PM)</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="p-8 border-t border-slate-100 dark:border-slate-800 flex gap-4">
                <button (click)="editFromDetails()"
                    class="h-[40px] px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-purple-600/20 dark:shadow-none text-sm">
                    Edit Release
                </button>
                <button (click)="deleteFromDetails()"
                    class="h-[40px] px-6 bg-white dark:bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 text-purple-600 font-medium rounded-xl border-2 border-purple-100 dark:border-purple-900 transition-all text-sm">
                    Delete
                </button>
            </div>
        </div>
    </ng-template>

    <!-- Audit Logs -->
    <div class="mt-8">
        <app-audit-log-panel [entityType]="auditEntityTypes" title="Release Audit Trails">
        </app-audit-log-panel>
    </div>
</div>